import {useMutation, useQueryClient} from '@tanstack/react-query';
import {DropboxClientContext} from 'App';
import {snackbarAtom} from 'atoms/snackbar';
import {DeleteModal} from 'components/DSYS/DeleteModal';
import {t} from 'i18next';
import {useSetAtom} from 'jotai';
import {ReactNode, useContext, useState} from 'react';

import {appsQueryKey, retireSite} from './api';
import {getAppDisplayName} from './appUtils';
import {CatalogApp} from './catalogApp';

export const AppDeletionAction = ({
  app,
  children,
}: {
  app: CatalogApp;
  children: (requestDelete: () => void) => ReactNode;
}) => {
  const client = useContext(DropboxClientContext);
  const queryClient = useQueryClient();
  const setSnackbar = useSetAtom(snackbarAtom);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const retireMutation = useMutation({
    mutationFn: async () => {
      if (!client || !app.id) {
        throw new Error('App retirement is unavailable');
      }
      return retireSite(client, {
        site_id: app.id,
        request_token: `retire-${globalThis.crypto.randomUUID()}`,
        expected_lifecycle: {'.tag': 'site_retirement_expected_lifecycle_active'},
        expected_record_version: 1,
      });
    },
  });

  return (
    <>
      {children(() => setConfirmDelete(true))}
      <DeleteModal
        open={confirmDelete}
        onClose={() => {
          if (!retireMutation.isPending) {
            setConfirmDelete(false);
          }
        }}
        title={t('apps_delete_title', {name: getAppDisplayName(app)})}
        subtitle={t('apps_delete_description')}
        isPending={retireMutation.isPending}
        onDelete={async () => {
          try {
            const response = await retireMutation.mutateAsync();
            void queryClient.invalidateQueries({queryKey: appsQueryKey});
            return response;
          } catch {
            setConfirmDelete(false);
            setSnackbar({text: t('apps_delete_error')});
            throw new Error('App deletion failed');
          }
        }}
      />
    </>
  );
};
